import { Box, Center, Loader } from '@mantine/core';
import { DateValue } from '@mantine/dates';
import { useCallback } from 'react';

import { useGetTriggersReport } from '../../api/getTriggersReport';
import { LABELS } from '../../constants';

import { TriggersAnalyticsTable } from './Table/AnalyticsTable';

import { useGetPages } from '@/features/pages';
import { Platforms } from '@/types';

type TriggersAnalyticsProps = {
  dates: [DateValue, DateValue];
  expertIds: string[];
  platform: Platforms | 'all';
};

export const TriggersAnalyticsData = ({ dates, expertIds, platform }: TriggersAnalyticsProps) => {
  const [from, to] = dates;

  const isPlatformEnabled = useCallback(
    (p: Platforms) => !platform || platform === Platforms[p] || platform === 'all',
    [platform]
  );

  const getTgPages = useGetPages({
    platform: Platforms.telegram,
    config: { enabled: isPlatformEnabled(Platforms.telegram) },
  });
  const getInstPages = useGetPages({
    platform: Platforms.instagram,
    config: { enabled: isPlatformEnabled(Platforms.instagram) },
  });

  const dateFrom = from?.valueOf() || 0;
  const dateTo = to?.valueOf() || 0;

  const getTriggersReport = useGetTriggersReport({
    dateFrom,
    dateTo,
    expertIds,
  });

  const tgPages = getTgPages.data?.pages ?? [];
  const instPages = getInstPages.data?.pages ?? [];

  const pagesMap: Record<string, { name: string; platform: Platforms }> = {};
  tgPages.forEach((tgPage) => {
    if (!pagesMap[tgPage._id]) {
      pagesMap[tgPage._id] = { name: tgPage.name, platform: Platforms.telegram };
    }
  });
  instPages.forEach((instPage) => {
    if (!pagesMap[instPage._id]) {
      pagesMap[instPage._id] = { name: instPage.name, platform: Platforms.instagram };
    }
  });

  if (!expertIds.length || !dateFrom || !dateTo) {
    return <Center>{LABELS.NO_FILTERS_SELECTION}</Center>;
  }

  if (getTriggersReport.error) {
    return <Center>{LABELS.DATA_LOAD_ERROR}</Center>;
  }

  if (getTriggersReport.isLoading) {
    return (
      <Center>
        <Loader m="xs" size="xl" />
      </Center>
    );
  }

  if (!getTriggersReport.data) {
    return <Center>{LABELS.NO_FILTER_DATA}</Center>;
  }

  return (
    <Box style={{ overflow: 'auto' }}>
      <TriggersAnalyticsTable data={getTriggersReport.data} pagesMap={pagesMap} />
    </Box>
  );
};
